<script setup lang="ts">
import { useSerialize } from "@lxc/app-device-common";
import type { Rules } from "async-validator";
import { ref, type Ref } from "vue";
import { useAcl } from "vue-simple-acl";
import { SearchMode } from "~/core/composables/useSearch";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { OrganizationType } from "~/modules/organization/models/OrganizationType.enum";
import { useCreateUser } from "~/modules/user/composables/useCreateUser";
import { useFetchProfiles } from "~/modules/user/composables/useFetchProfiles";
import { useFetchUserGroups } from "~/modules/user/composables/useFetchUserGroups";
import { useGetUser } from "~/modules/user/composables/useGetUser";
import { useUpdateUser } from "~/modules/user/composables/useUpdateUser";
import type { UserCreateDto } from "~/modules/user/models/UserCreateDto.interface";
import type { UserSessionSector } from "~/modules/user/models/UserSessionSector.interface";
import type { UserUpdateDto } from "~/modules/user/models/UserUpdateDto.interface";
import { getProfileLabel } from "~/modules/user/utils/profileLabel";
import { getUserValidationRules } from "~/modules/user/utils/userValidationRules";

const { t } = useI18n();
const serialize = useSerialize();
const { can } = useAcl();

const props = defineProps<{
  show: boolean;
  businessUnitCode?: number;
  organizationCode?: number;
  organizationType?: OrganizationType;
  user?: string;
  useExternalIdp?: boolean;
}>();
const { results: profileResults, fetchData: profileFetchAll } =
  useFetchProfiles({
    businessUnitCode: props.businessUnitCode,
    organizationCode: props.organizationCode,
    searchMode: SearchMode.FILTER_SEARCH,
  });
const { createUser, isLoading, error } = useCreateUser({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
});
const {
  updateUser,
  isLoading: isLoadingUpdate,
  error: errorUpdate,
} = useUpdateUser({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
});
const {
  getUser,
  isLoading: isLoadingGetUser,
  error: errorGetUser,
  result: resultGetUser,
} = useGetUser({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
});
const {
  resultsFetchAll: groupsResult,
  fetchAll: groupsFetchAll,
  isLoadingAllGroups: isLoadingGetGroups,
} = useFetchUserGroups({
  organizationCode: props.organizationCode,
  searchMode: SearchMode.FILTER_SEARCH,
});

const emit = defineEmits(["update:show", "close"]);

const edited: Ref<boolean> = ref(false);
const showCanvas: WritableComputedRef<boolean> = computed({
  get: () => {
    return props.show;
  },
  set: (shown: boolean) => {
    emit("update:show", shown);
  },
});

const labelButtonSave = computed(() =>
  props.user ? t("button.save") : t("user.create.saveButton"),
);

const userForm: Partial<UserCreateDto> = reactive({
  email: "",
  firstName: "",
  lastName: "",
  language: "",
  login: "",
  profile: "",
  groups: [],
  sectors: [],
});
const userFormRef: Ref<HTMLFormElement | undefined | null> = ref();
let initFormStringified: Ref<string> = ref(serialize(userForm));
const formHasChanged: ComputedRef<boolean> = computed(
  () => serialize(userForm) !== initFormStringified.value,
);
const formRules: Rules = {
  ...getUserValidationRules(t),
  profile: [
    {
      required: true,
      message: t("user.profileRequired"),
    },
  ],
};
const isFormValid: ComputedRef<boolean | undefined> = computed(
  () => userFormRef.value?.isValid,
);
const showGroups: ComputedRef<boolean> = computed(
  () => props.organizationType === OrganizationType.OPERATOR,
);
const showSectors: ComputedRef<boolean> = computed(() => {
  return (
    props.organizationType === OrganizationType.OPERATOR &&
    can(ACLRoles.CAN_READ_SECTOR)
  );
});

function onSuccess() {
  showNotificationSuccess(t(NotificationKey.saveSuccess));
  edited.value = true;
  onClose();
}

async function onSubmit() {
  if (isFormValid.value) {
    const userRequest: UserUpdateDto = {
      firstName: userForm.firstName!,
      lastName: userForm.lastName!,
      email: userForm.email!,
      active: true,
      profile: userForm.profile!,
      language: userForm.language!,
    };
    if (showGroups.value) {
      userRequest.groups = toRaw(userForm.groups);
    }

    if (showSectors.value) {
      userRequest.sectors = userForm.sectors;
    }

    if (props.user) {
      await updateUser(props.user!, userRequest);
      if (errorUpdate.value) {
        errorUpdate.value.notify(NotificationKey.saveError);
      } else {
        onSuccess();
      }
    } else {
      const userRequestCreate: UserCreateDto = {
        ...userRequest,
        login: userForm.login!,
      };
      await createUser(userRequestCreate);
      if (error.value) {
        error.value.notify(NotificationKey.saveError);
      } else {
        onSuccess();
      }
    }
  }
}

function onClose() {
  showCanvas.value = false;
  clearForm();
  emit("close", {
    edited: edited.value,
  });
  edited.value = false;
}

function clearForm() {
  userForm.email = "";
  userForm.firstName = "";
  userForm.lastName = "";
  userForm.language = "";
  userForm.login = "";
  userForm.profile = "";
  userForm.groups = [];
  userForm.sectors = [];
  initFormStringified.value = serialize(userForm);
  setTimeout(() => {
    userFormRef.value?.clearValidate();
  }, 0);
}

async function fetchUser() {
  if (props.user) {
    await getUser(props.user);
    if (!errorGetUser.value) {
      userForm.login = props.user;
      userForm.lastName = resultGetUser.value?.lastName;
      userForm.firstName = resultGetUser.value?.firstName;
      userForm.email = resultGetUser.value?.email;
      userForm.profile = resultGetUser.value?.profile.id;
      userForm.language = resultGetUser.value?.language;
      userForm.groups =
        resultGetUser.value?.groups?.map((group) => group.name) ?? [];
      userForm.sectors =
        resultGetUser.value?.sectors?.map((sector: UserSessionSector) =>
          sector.code.toString(),
        ) ?? [];
      initFormStringified.value = serialize(userForm);
    }
  }
}

onMounted(() => {
  profileFetchAll();
  if (showGroups.value) {
    groupsFetchAll();
  }

  fetchUser();
});

watch(
  () => [props.user, props.show] as const,
  async ([user, showCanvas]) => {
    if (user && showCanvas) {
      await fetchUser();
    } else {
      errorGetUser.value = null;
    }
  },
);
</script>

<template>
  <lxc-side-canvas
    v-model:show="showCanvas"
    :header="user ? t('user.update.title') : t('user.create.title')"
    size="1/2"
    :confirm-title="
      user ? t('user.update.confirm.title') : t('user.create.confirm.title')
    "
    :confirm-message="t('user.confirm.message')"
    :confirm-ok-label="t('button.confirm')"
    :confirm-cancel-label="t('button.cancel')"
    :confirm-enabled="formHasChanged"
    @hidden="onClose"
  >
    <container-component
      :py="0"
      :px="0"
      :is-loading="isLoadingGetUser"
      :error="errorGetUser"
    >
      <lxc-form
        ref="userFormRef"
        :rules="formRules"
        :model="userForm"
        @submit.prevent="onSubmit"
      >
        <h3 class="mb-6">
          {{ t("user.create.information") }}
        </h3>
        <div class="grid gap-x-8 grid-cols-2">
          <lxc-form-item
            :label="t('user.lastName')"
            prop="lastName"
            class="basis-1/2"
          >
            <lxc-input
              v-model="userForm.lastName"
              type="text"
              :disabled="useExternalIdp"
            />
          </lxc-form-item>
          <lxc-form-item
            :label="t('user.firstName')"
            prop="firstName"
            class="basis-1/2"
          >
            <lxc-input
              v-model="userForm.firstName"
              type="text"
              :disabled="useExternalIdp"
            />
          </lxc-form-item>
          <lxc-form-item
            :label="t('user.login')"
            prop="login"
            class="basis-1/2"
          >
            <lxc-input
              v-model="userForm.login"
              type="text"
              :disabled="!!user"
            />
          </lxc-form-item>
          <lxc-form-item
            :label="t('user.email')"
            prop="email"
            class="basis-1/2"
          >
            <lxc-input
              v-model="userForm.email"
              type="email"
              :disabled="useExternalIdp"
            />
          </lxc-form-item>
          <lxc-form-item
            :label="t('user.language')"
            prop="language"
            class="basis-1/2"
          >
            <language-component v-model="userForm.language" />
          </lxc-form-item>
          <lxc-form-item
            :label="t('user.profile')"
            prop="profile"
            class="pt-2 col-start-1"
          >
            <lxc-select
              v-model="userForm.profile"
              label-class="pb-2 font-semibold inline-block text-base !text-gray-900"
              select-filter
              :select-search-placeholder="t('user.profilePlaceholder')"
              :select-no-result-text="t('input.select.search.noResults')"
            >
              <lxc-option
                v-for="item in profileResults"
                :key="item.id"
                :label="getProfileLabel(item)"
                :value="item.id"
                :disabled="!item.isAuthorized"
              />
            </lxc-select>
          </lxc-form-item>
          <lxc-form-item
            v-if="showGroups"
            :label="t('user.groups')"
            prop="groups"
            class="pt-2 col-start-1"
          >
            <lxc-select
              v-model="userForm.groups"
              label-class="pb-2 font-semibold inline-block text-base !text-gray-900"
              multiple
              select-filter
              selected-as-tag
              :select-all-label="t('user.form.userGroups.selectAllLabel')"
              :select-search-placeholder="t('user.groupsSearchPlaceholder')"
              :select-no-result-text="t('input.select.search.noResults')"
              :is-loading-options="isLoadingGetGroups"
              :is-loading-selected="isLoadingGetUser"
            >
              <lxc-option
                v-for="item in groupsResult"
                :key="item.name"
                :label="item.name"
                :value="item.name"
                >{{ item.name }}</lxc-option
              >
            </lxc-select>
          </lxc-form-item>
          <lxc-form-item
            v-if="showSectors"
            :label="t('user.sectors')"
            label-class="pb-2 font-semibold inline-block text-base !text-gray-900"
            prop="sectors"
            class="pt-2 col-start-1"
            :display-label="true"
          >
            <user-sector-selection
              v-model="userForm.sectors"
              :organization-code="organizationCode"
            />
          </lxc-form-item>
        </div>
      </lxc-form>
    </container-component>

    <template #footer>
      <div class="flex gap-6">
        <lxc-button
          html-type="button"
          type="tertiary"
          :text="t('button.cancel')"
          :title="t('button.cancel')"
          @click="onClose"
        />
        <lxc-button
          html-type="button"
          type="primary"
          class="grow"
          :text="labelButtonSave"
          :title="labelButtonSave"
          :is-loading="isLoading || isLoadingUpdate"
          :disabled="!isFormValid || !formHasChanged"
          @click="onSubmit"
        />
      </div>
    </template>
  </lxc-side-canvas>
</template>
